import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@codes/ui-kit/components/ui/button';
import { Card } from '@codes/ui-kit/components/ui/card';
import { Input } from '@codes/ui-kit/components/ui/input';
import { toast } from '@codes/ui-kit/components/ui/use-toast';
import { ImageIcon } from 'lucide-react';

import { useUpdateProfile, useUpdateUserPhoto } from '@codes/auth';
import { useLoader } from '@codes/loader';
import { Avatar, AvatarFallback, AvatarImage, Label, SharedSpinner } from '@codes/ui-kit';
import { UserView, useWebCoreStore } from '@codes/web-core';

interface NameEditFormProps {
    currentName: string;
    ownerId: string;
    onSuccess?: (user: UserView) => void;
}

export const NameEditForm = ({ currentName, ownerId, onSuccess }: NameEditFormProps) => {
    const { t } = useTranslation();
    const [name, setName] = useState(currentName);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const { setIsLoading } = useLoader();
    const inputRef = useRef<HTMLInputElement>(null);
    const updateUserPhotoFromStore = useWebCoreStore(state => state.updateUserPhoto);

    const profile = useWebCoreStore(state => state.profile);
    const { mutate: updateProfile } = useUpdateProfile(
        (user: UserView) => {
            setIsSubmitting(false);
            onSuccess?.(user);
        },
        error => {
            setIsSubmitting(false);
            toast({
                title: error?.message,
            });
        }
    );

    const { mutateAsync: updateUserPhoto } = useUpdateUserPhoto(
        () => {},
        error => {
            setIsSubmitting(false);
            toast({
                title: error?.message,
            });
        }
    );

    const handleNameUpdate = () => {
        setIsSubmitting(true);
        updateProfile({ name: name.trim(), id: profile.uid });
    };

    const imageBtnTapped = () => {
        inputRef.current?.click();
    };

    const handleFileInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target?.files?.[0];
        e.target.value = '';

        if (!file) {
            return;
        }
        setIsLoading(true);

        try {
            const user = await updateUserPhoto({ file, userId: profile.uid });
            updateUserPhotoFromStore(user);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (currentName) {
            setName(currentName);
        }
    }, [currentName]);

    return (
        <Card className="dark:bg-[#2e2e2e]">
            <div className="justify-center flex my-5">
                <div className="relative">
                    <button
                        onClick={imageBtnTapped}
                        className="w-7 h-7 rounded-full bg-black/30 absolute bottom-1 right-3 z-50 flex items-center justify-center"
                    >
                        <ImageIcon size={16} className="text-white" />
                    </button>
                    <input
                        type="file"
                        onChange={handleFileInputChange}
                        className="hidden"
                        ref={inputRef}
                        accept="image/*"
                    />
                    <Avatar className="w-32 h-32 bg-[#BBC5CD] dark:bg-[#595F65] p-0">
                        <AvatarImage src={profile?.$user?.photo} alt="@shadcn" />
                        <AvatarFallback></AvatarFallback>
                    </Avatar>
                </div>
            </div>
            <Label variant="form">{t('myPage.name')}</Label>
            <Input
                placeholder={t('myPage.enterName')}
                value={name}
                onChange={e => setName(e.target.value)}
                disabled={isSubmitting}
            />
            <Button
                size="lg"
                className="w-full disabled:bg-badge mt-10"
                onClick={handleNameUpdate}
                disabled={isSubmitting || !name.trim() || name.trim() === currentName}
            >
                {isSubmitting ? <SharedSpinner /> : t('myPage.edit')}
            </Button>
        </Card>
    );
};
